import type { Composer } from 'vue-i18n'
import type { BreakdownAxis, Concentration, OpportunitySummary, PriorityRationale, ShareRationale } from '@/api/opportunities'
import { formatDayRange } from './metric-format'
import { formatRate } from './continuity-format'

/**
 * How an opportunity is written, in the reader's language: its sentences
 * come from the rule's codes and figures (P3 §2.4: never stored as prose),
 * and every number and date is formatted here and interpolated.
 */

type Translate = Composer['t']

const DAY_MS = 86_400_000
/** A Monday, from which ISO weekdays 1–7 are counted. */
const A_MONDAY = Date.UTC(2026, 8, 28)

/** A calendar day moved by `days`, `YYYY-MM-DD`. */
export const addDays = (day: string, days: number): string =>
  new Date(Date.parse(`${day}T00:00:00.000Z`) + days * DAY_MS).toISOString().slice(0, 10)

/** Minutes to a tenth, as the rule compares them: "24.4 min". */
export const formatMinutes = (t: Translate, minutes: number, locale: string): string =>
  t('overview.units.minutes', { value: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(minutes) })

/** A signed change in minutes: "+14 min". */
export const formatRise = (t: Translate, minutes: number, locale: string): string =>
  t('overview.units.minutes', {
    value: new Intl.NumberFormat(locale, { maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(minutes),
  })

/** A whole percentage, as the rule rounds it (down): "58%". */
export const formatPercent = (percent: number, locale: string, signed = false): string =>
  new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0, signDisplay: signed ? 'exceptZero' : 'auto' }).format(
    percent / 100,
  )

/** A share the next-step rule compares, in percent to a tenth as it is stored: "76.4%". */
export const formatSharePercent = (share: number, locale: string): string => formatRate(share / 100, locale, 1)

/** Percentage points to a tenth: "10.4 pts". */
export const formatPoints = (t: Translate, points: number, locale: string): string =>
  t('overview.continuity.points', { value: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(points) })

/** A week by its Monday: "14 Sep". */
export const formatWeek = (monday: string, locale: string): string => formatDayRange(monday, monday, locale)

/** The weeks from one Monday to the Sunday after another: "1 – 28 Sep". */
export const formatWeeks = (firstMonday: string, lastMonday: string, locale: string): string =>
  formatDayRange(firstMonday, addDays(lastMonday, 6), locale)

/** An ISO weekday (1 is Monday) by name. */
export const weekdayName = (isoDay: number, locale: string): string =>
  new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(new Date(A_MONDAY + (isoDay - 1) * DAY_MS))

/** The start of an hour of the day, on the reader's clock face: "08:00". */
export const hourName = (hour: number, locale: string): string =>
  new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }).format(new Date(A_MONDAY + hour * 3_600_000))

/** One group of a breakdown, as its table row names it. */
export const groupLabel = (t: Translate, axis: BreakdownAxis, group: string, locale: string): string => {
  if (axis === 'DAY') return weekdayName(Number(group), locale)
  if (axis === 'HOUR') return t('opportunities.hourRange', { from: hourName(Number(group), locale), to: hourName((Number(group) + 1) % 24, locale) })
  if (axis === 'ROLE') return t(`opportunities.roles.${group}`)
  return t(`opportunities.visitTypes.${group}`)
}

/**
 * "Why surfaced", from the priority rule's line and figures: PRIORITY-RULE-1
 * for waiting time, PRIORITY-RULE-CARE-1 for next steps (DECISIONS.md #45).
 * The sentence says "last week" only when the week it refers to is the last
 * one that ended; otherwise it names the week (Dao, 30 Sep 2026).
 */
export const rationaleText = (t: Translate, rationale: PriorityRationale | ShareRationale, lastCompleteWeek: string, locale: string): string => {
  const recent = rationale.week === lastCompleteWeek
  const week = formatWeek(rationale.week, locale)
  switch (rationale.code) {
    case 'WORSE_WEEKS_IN_A_ROW':
      return recent
        ? t('opportunities.rationale.weeksInARow', { weeks: rationale.weeks })
        : t('opportunities.rationale.weeksInARowUpTo', { weeks: rationale.weeks, week })
    case 'ABOVE_USUAL':
      return recent
        ? t('opportunities.rationale.aboveUsual', { percent: formatPercent(rationale.percent, locale) })
        : t('opportunities.rationale.aboveUsualInWeek', { percent: formatPercent(rationale.percent, locale), week })
    case 'WORSE_LAST_WEEK':
      return recent ? t('opportunities.rationale.worseLastWeek') : t('opportunities.rationale.worseInWeek', { week })
    case 'FEWER_WEEKS_IN_A_ROW':
      return recent
        ? t('opportunities.rationale.fewerWeeksInARow', { weeks: rationale.weeks })
        : t('opportunities.rationale.fewerWeeksInARowUpTo', { weeks: rationale.weeks, week })
    case 'BELOW_USUAL':
      return recent
        ? t('opportunities.rationale.belowUsual', { points: formatPoints(t, rationale.points, locale) })
        : t('opportunities.rationale.belowUsualInWeek', { points: formatPoints(t, rationale.points, locale), week })
    case 'FEWER_LAST_WEEK':
      return recent ? t('opportunities.rationale.fewerLastWeek') : t('opportunities.rationale.fewerInWeek', { week })
  }
}

/** What changed, as the card and the Investigation view head it: from the usual to the latest worse or lower week. */
export const headlineText = (t: Translate, opportunity: OpportunitySummary, locale: string): string => {
  const key = `opportunities.metrics.${opportunity.metricId}.headline`
  if (opportunity.kind === 'NEXT_STEP') {
    return t(key, { from: formatSharePercent(opportunity.baselineShare, locale), to: formatSharePercent(opportunity.latestShare, locale) })
  }
  return t(key, { from: formatMinutes(t, opportunity.baselineMinutes, locale), to: formatMinutes(t, opportunity.latestMinutes, locale) })
}

/** An opportunity's figures, from the feed or from Elfie Pulse: a share for next steps, minutes for waiting time. */
export type WeekFigures =
  | { rationale: ShareRationale; latestShare: number; baselineShare: number }
  | { rationale: PriorityRationale; latestMinutes: number; baselineMinutes: number }

/**
 * An opportunity's figures as one sentence that names its week (Dao, 1 Oct
 * 2026): the latest worse or lower week against the usual, so it is never
 * read as the period on the page around it. The rationale's week is that
 * week: the priority rule is worked out on each such week as it comes.
 */
export const weekFiguresText = (t: Translate, opportunity: WeekFigures, locale: string): string => {
  const week = formatWeek(opportunity.rationale.week, locale)
  if ('latestShare' in opportunity) {
    const { rationale } = opportunity
    const values = { week, from: formatSharePercent(opportunity.baselineShare, locale), to: formatSharePercent(opportunity.latestShare, locale) }
    switch (rationale.code) {
      case 'BELOW_USUAL':
        return t('opportunities.inWeek.belowUsual', { ...values, points: formatPoints(t, rationale.points, locale) })
      case 'FEWER_WEEKS_IN_A_ROW':
        return t('opportunities.inWeek.fewerWeeksInARow', { ...values, weeks: rationale.weeks })
      case 'FEWER_LAST_WEEK':
        return t('opportunities.inWeek.fewer', values)
    }
  }
  const { rationale } = opportunity
  const values = { week, from: formatMinutes(t, opportunity.baselineMinutes, locale), to: formatMinutes(t, opportunity.latestMinutes, locale) }
  switch (rationale.code) {
    case 'ABOVE_USUAL':
      return t('opportunities.inWeek.aboveUsual', { ...values, percent: formatPercent(rationale.percent, locale) })
    case 'WORSE_WEEKS_IN_A_ROW':
      return t('opportunities.inWeek.weeksInARow', { ...values, weeks: rationale.weeks })
    case 'WORSE_LAST_WEEK':
      return t('opportunities.inWeek.worse', values)
  }
}

/**
 * The usual level and the latest worse or lower week's, in the opportunity's
 * unit, with the labels the Investigation view gives them. A report frozen
 * before next steps were found carries no `kind`: it is a waiting time.
 */
export const usualAndLatest = (t: Translate, opportunity: WeekFigures, locale: string): { usual: string; latest: string; usualLabel: string; latestLabel: string } =>
  'latestShare' in opportunity
    ? {
        usual: formatSharePercent(opportunity.baselineShare, locale),
        latest: formatSharePercent(opportunity.latestShare, locale),
        usualLabel: t('opportunities.nextStep.detail.usual'),
        latestLabel: t('opportunities.nextStep.detail.latest'),
      }
    : {
        usual: formatMinutes(t, opportunity.baselineMinutes, locale),
        latest: formatMinutes(t, opportunity.latestMinutes, locale),
        usualLabel: t('opportunities.detail.usual'),
        latestLabel: t('opportunities.detail.latest'),
      }

/** Who an opportunity's weeks concern: the visits measured in them, or the chronic patients still without a next step. */
export const affectedText = (t: Translate, opportunity: OpportunitySummary): string =>
  opportunity.kind === 'NEXT_STEP'
    ? t('opportunities.nextStep.affected', { n: opportunity.withoutNextStep }, opportunity.withoutNextStep)
    : t('opportunities.affected', { n: opportunity.affectedVisits }, opportunity.affectedVisits)

/** Where the increase is concentrated, as one whole sentence per case: never assembled from pieces. */
export const concentrationText = (t: Translate, concentration: Concentration, locale: string): string => {
  const { axis, groups } = concentration
  if (axis === 'HOUR') {
    const first = Number(groups[0])
    const last = Number(groups[groups.length - 1])
    return t('opportunities.concentration.HOUR', { from: hourName(first, locale), to: hourName((last + 1) % 24, locale) })
  }
  return t(`opportunities.concentration.${axis}.${concentration.top}`)
}
